import { FormEvent, useEffect, useState } from 'react';
import cx from 'classnames';
import styled from 'styled-components';

import { ContentWidthWrapper } from '@pass-frontend/common/components/content-width-wrapper/ui';
import { useIntl } from '@pass-frontend/common/i18n';
import { MemberCheckStatus } from '@pass-frontend/data-access/common/edge';
import { useEligibilityCheck } from '@pass-frontend/features/standalone/data-bridge';
import { MEDIA_QUERY } from '@pass-frontend/utility/style-helpers';

import { MemberCodeFoundModal } from '../member-code-found/member-code-found';
import { MemberCodeNotFoundModal } from '../member-code-not-found/member-code-not-found';

const StyledEligibilityCheckForm = styled.div`
  h1 {
    margin-top: 32px;
    margin-bottom: 8px;
  }

  p {
    margin-bottom: 24px;
  }

  .name {
    flex-direction: column;
  }

  input:focus-visible {
    outline: none;
    border-color: #222222;
  }

  .btn {
    width: 100%;
  }

  .loading {
    background-color: #b1b2b4;
  }

  .disabled {
    pointer-events: none;
  }

  ${MEDIA_QUERY.desktop} {
    width: 616px;
    margin: auto;

    h1 {
      margin-top: 56px;
      margin-bottom: 8px;
    }

    p {
      margin-bottom: 32px;
    }

    .name {
      flex-direction: row;

      input {
        width: 296px;
      }
    }

    .btn {
      width: 160px;
      font-size: 16px;
      line-height: 20px;
      padding: 10px 0;
    }
  }
`;

const StyledInput = styled.div.attrs({
  className: 'is-display-flex is-flex-direction-column',
})`
  margin-bottom: 16px;

  label {
    margin-bottom: 4px;
  }

  input {
    border: 1px solid #b3b3b3;
    border-radius: 32px;
    height: 48px;
    padding: 14px 16px;
  }

  ${MEDIA_QUERY.desktop} {
    margin-bottom: 24px;

    label {
      margin-bottom: 8px;
    }

    &:first-child {
      margin-right: 24px;
    }
  }
`;

export function MemberCheckForm() {
  const { t } = useIntl();

  const {
    fetchMemberByEligibility,
    serviceSector,
    memberCode,
    isNotEligibleModalOpen,
    memberCheckInProgress,
    isEligibleModalOpen,
    eligibilityCheckStatus,
    onConfirmModalClose,
  } = useEligibilityCheck();

  const [shouldShowAdditionalFields, setShouldShowAdditionalFields] =
    useState(false);

  const [eligibilityForm, setEligibilityForm] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
    searchId: '',
  });

  useEffect(() => {
    if (
      eligibilityCheckStatus === MemberCheckStatus.internalServerError ||
      eligibilityCheckStatus === MemberCheckStatus.tooManyMatches
    ) {
      setShouldShowAdditionalFields(true);
    }
  }, [eligibilityCheckStatus]);

  const isSubmitDisabled =
    !eligibilityForm.firstName ||
    !eligibilityForm.lastName ||
    !eligibilityForm.birthDate ||
    (shouldShowAdditionalFields && !eligibilityForm.searchId) ||
    memberCheckInProgress;

  const setFormValue = (field: string, value: string) => {
    setEligibilityForm((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleBirthdateInput = (e) => {
    let input = e.target.value;
    if (input.length < eligibilityForm.birthDate.length) {
      if (
        eligibilityForm.birthDate[eligibilityForm.birthDate.length - 1] === '/'
      ) {
        input = input.slice(0, input.length - 1);
      }
    }
    input = input.replace(/[^0-9]/g, '');
    if (input.length >= 2 && input.length < 4) {
      input = `${input.slice(0, 2)}/${input.slice(2)}`;
    } else if (input.length >= 4) {
      input = `${input.slice(0, 2)}/${input.slice(2, 4)}/${input.slice(4, 8)}`;
    }
    setFormValue('birthDate', input);
  };

  const onEligibilitySubmit = (event: FormEvent) => {
    event.preventDefault();

    if (!isSubmitDisabled) {
      fetchMemberByEligibility(
        {
          firstName: eligibilityForm.firstName,
          lastName: eligibilityForm.lastName,
          birthDate: eligibilityForm.birthDate,
          searchId: eligibilityForm.searchId,
        },
        '',
        false,
      );
    }
  };

  return (
    <ContentWidthWrapper>
      <StyledEligibilityCheckForm>
        <h1 className="is-h1 has-text-blue-1">{t('memberCode.form.title')}</h1>
        <form
          onSubmit={(e: FormEvent) => onEligibilitySubmit(e)}
          id="submit-eligiblity"
        >
          <div className="is-display-flex name">
            <StyledInput>
              <label className="is-body-2-regular" htmlFor="first">
                {t('eligibilityCheck.form.firstName')}
              </label>
              <input
                type="text"
                id="firstName"
                name="first"
                autoComplete="do-not-autofill"
                value={eligibilityForm.firstName}
                onChange={(e) => setFormValue('firstName', e.target.value)}
              />
            </StyledInput>
            <StyledInput>
              <label className="is-body-2-regular" htmlFor="last">
                {t('eligibilityCheck.form.lastName')}
              </label>
              <input
                type="text"
                id="lastName"
                name="last"
                value={eligibilityForm.lastName}
                onChange={(e) => setFormValue('lastName', e.target.value)}
              />
            </StyledInput>
          </div>
          <StyledInput>
            <label className="is-body-2-regular" htmlFor="date-of-birth">
              {t('eligibilityCheck.form.dateOfBirth')}
            </label>
            <input
              type="text"
              id="date-of-birth"
              name="dateOfBirth"
              placeholder="MM/DD/YYYY"
              value={eligibilityForm.birthDate}
              onChange={handleBirthdateInput}
            />
          </StyledInput>
          {shouldShowAdditionalFields && (
            <StyledInput>
              <label className="is-body-2-regular" htmlFor="health-plan-id">
                {t('memberCode.form.healthPlanID')}
              </label>
              <input
                type="text"
                id="health-plan-id"
                name="searchId"
                value={eligibilityForm.searchId}
                onChange={(e) => setFormValue('searchId', e.target.value)}
              />
            </StyledInput>
          )}
          <input
            type="submit"
            data-test-id="submit-eligiblity"
            value={
              memberCheckInProgress
                ? t('eligibilityCheck.submittingButtonText')
                : t('eligibilityCheck.submitButtonText')
            }
            disabled={isSubmitDisabled}
            className={cx('btn btn-primary', {
              disabled: isSubmitDisabled,
              loading: memberCheckInProgress,
            })}
          />
        </form>
        <MemberCodeFoundModal
          isOpen={isEligibleModalOpen}
          onRequestedClose={() => {
            onConfirmModalClose();
          }}
          aria-modal={true}
          memberCode={memberCode}
          serviceSector={serviceSector}
        />
        <MemberCodeNotFoundModal
          isOpen={isNotEligibleModalOpen}
          onRequestedClose={() => {
            onConfirmModalClose();
          }}
          aria-modal={true}
        />
      </StyledEligibilityCheckForm>
    </ContentWidthWrapper>
  );
}
